import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { DataTable } from "@/components/tables/DataTable";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { useAuthUser } from "@/features/auth/useAuth";
import { useAdmins, useCreateAdmin, useDeleteAdmin, useRoles, useUpdateAdmin } from "@/features/team/useTeam";
import { toast } from "@/store/toast.store";
import { getApiErrorMessage } from "@/api/client";
import { formatDateTime } from "@/utils/format";
import type { AdminAccount } from "@/types/auth";

const schema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
  roleId: z.string().min(1, "Choose a role"),
});
type FormValues = z.infer<typeof schema>;

export default function TeamAdmins() {
  const { user } = useAuthUser();
  const admins = useAdmins();
  const roles = useRoles();
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const deleteAdmin = useDeleteAdmin();

  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminAccount | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Only active roles are offerable when assigning someone — an inactive
  // role can still show on existing admins (handled per-row below) so a
  // deactivated role's assignees stay visible rather than vanishing.
  const assignableRoles = useMemo(() => (roles.data ?? []).filter((r) => r.active), [roles.data]);

  const columns = useMemo<ColumnDef<AdminAccount, unknown>[]>(
    () => [
      { header: "Name", accessorKey: "name" },
      { header: "Email", accessorKey: "email" },
      {
        header: "Role",
        cell: ({ row }) => {
          const admin = row.original;
          const isSelf = admin.id === user?.id;
          const value = admin.customRoleId ?? "SUPER_ADMIN";
          // The admin's current role stays selectable even if it was since
          // deactivated, so the dropdown never silently shows the wrong thing.
          const options =
            admin.customRoleId && !assignableRoles.some((r) => r.id === admin.customRoleId)
              ? [{ id: admin.customRoleId, name: `${admin.roleName} (inactive)` }, ...assignableRoles]
              : assignableRoles;
          return (
            <Select
              value={value}
              disabled={isSelf || updateAdmin.isPending}
              title={isSelf ? "Ask another Super Admin to change your own role" : undefined}
              onChange={(e) =>
                updateAdmin.mutate(
                  { id: admin.id, roleId: e.target.value },
                  {
                    onSuccess: () => toast.success("Role updated"),
                    onError: (err) => toast.error(getApiErrorMessage(err)),
                  },
                )
              }
            >
              <option value="SUPER_ADMIN">Super Admin</option>
              {options.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          );
        },
      },
      {
        header: "Status",
        cell: ({ row }) => {
          const admin = row.original;
          const isSelf = admin.id === user?.id;
          return (
            <div className="flex items-center gap-2">
              <Badge tone={admin.active ? "teal" : "neutral"}>
                {admin.active ? "Active" : "Inactive"}
              </Badge>
              {!isSelf && (
                <Button
                  variant="outline"
                  size="sm"
                  loading={updateAdmin.isPending}
                  onClick={() =>
                    updateAdmin.mutate(
                      { id: admin.id, active: !admin.active },
                      {
                        onSuccess: () =>
                          toast.success(admin.active ? "Admin deactivated" : "Admin activated"),
                        onError: (err) => toast.error(getApiErrorMessage(err)),
                      },
                    )
                  }
                >
                  {admin.active ? "Deactivate" : "Activate"}
                </Button>
              )}
            </div>
          );
        },
      },
      {
        header: "Last login",
        accessorFn: (a) => (a.lastLoginAt ? formatDateTime(a.lastLoginAt) : "—"),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => {
          const admin = row.original;
          const isSelf = admin.id === user?.id;
          return (
            !isSelf && (
              <button
                className="rounded-md p-1.5 text-ink-faint hover:bg-cream-200 hover:text-danger-500"
                title="Delete permanently"
                onClick={() => setDeleting(admin)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )
          );
        },
      },
    ],
    [user?.id, updateAdmin, assignableRoles],
  );

  return (
    <PageContainer
      title="Admins"
      description="Every admin account, and the role each one is assigned."
      actions={
        <Button
          size="sm"
          disabled={assignableRoles.length === 0}
          title={assignableRoles.length === 0 ? "Create a role first" : undefined}
          onClick={() => {
            reset({ name: "", email: "", password: "", roleId: assignableRoles[0]?.id ?? "" });
            setCreating(true);
          }}
        >
          New admin
        </Button>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={admins.data ?? []}
          isLoading={admins.isLoading}
          error={admins.error}
          onRetry={() => admins.refetch()}
          rowKey={(a) => a.id}
          emptyTitle="No admins yet"
        />
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New admin"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button form="new-admin-form" type="submit" variant="primary" loading={createAdmin.isPending}>
              Create
            </Button>
          </>
        }
      >
        <form
          id="new-admin-form"
          className="flex flex-col gap-3"
          onSubmit={handleSubmit((values) =>
            createAdmin.mutate(values, {
              onSuccess: () => {
                toast.success("Admin created");
                setCreating(false);
              },
              onError: (err) => toast.error(getApiErrorMessage(err)),
            }),
          )}
        >
          <FormField label="Name" error={errors.name?.message}>
            <Input error={!!errors.name} {...register("name")} />
          </FormField>
          <FormField label="Email" error={errors.email?.message}>
            <Input type="email" error={!!errors.email} {...register("email")} />
          </FormField>
          <FormField label="Password" error={errors.password?.message}>
            <Input type="password" error={!!errors.password} {...register("password")} />
          </FormField>
          <FormField label="Role" error={errors.roleId?.message}>
            <Select {...register("roleId")}>
              {assignableRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormField>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${deleting?.name ?? "admin"}?`}
        description="This permanently removes their account and signs them out everywhere. This cannot be undone."
        confirmLabel="Delete"
        loading={deleteAdmin.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteAdmin.mutate(deleting.id, {
            onSuccess: () => {
              toast.success("Admin deleted");
              setDeleting(null);
            },
            onError: (err) => {
              toast.error(getApiErrorMessage(err));
              setDeleting(null);
            },
          });
        }}
      />
    </PageContainer>
  );
}
